import {Stack} from "@mui/material";
import {AddOutlined, RemoveOutlined} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import React, {Dispatch, FC, SetStateAction, useEffect} from "react";
import {getTextColorOnRecommended} from "../../../../../../util/utils";
import TextField from "@mui/material/TextField";
import {validateQtyInput} from "../../../../../../util/regex";
import {Swiper as SwiperType} from "swiper";
import {alpha} from "@mui/material/styles";

interface Props {
    qty: any
    setQty: Dispatch<SetStateAction<any>>
    recommended: boolean
    max?: number
    size?: 'small' | 'medium' | 'large'
    swiperInstance?: SwiperType
}

const MAX_QTY_LIMIT = 999

const clampQty = (value: number, max: number) => Math.max(1, Math.min(value, max))

const QtySelector: FC<Props> = ({
                                    size = 'small',
                                    qty,
                                    setQty,
                                    recommended,
                                    max,
                                    swiperInstance
                                }) => {
    const textColor = getTextColorOnRecommended(recommended)
    const remainingQty = Number(max)
    const effectiveMax = Number.isFinite(remainingQty)
        ? Math.max(1, Math.min(remainingQty, MAX_QTY_LIMIT))
        : MAX_QTY_LIMIT
    const normalizedQty = Number(qty)
    const currentQty = clampQty(Number.isFinite(normalizedQty) ? normalizedQty : 1, effectiveMax)

    const typedDigits = String(qty ?? "").replace(/\D/g, "").length
    const inputVisibleCharacters = Math.max(2, typedDigits || 1)

    useEffect(() => {
        if (qty === "") {
            return
        }

        if (Number.isFinite(normalizedQty) && normalizedQty !== currentQty) {
            setQty(currentQty)
        }
    }, [currentQty, normalizedQty, qty, setQty])

    const stopSwiperAutoplay = () => {
        swiperInstance?.autoplay?.stop()
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim()
        if (!value) {
            setQty("")
            return
        }

        if (!validateQtyInput(value)) {
            return
        }

        const nextQty = Number(value)
        if (nextQty >= 1 && nextQty <= effectiveMax) {
            setQty(nextQty)
        }
    }

    const handleInputBlur = () => {
        const normalizedQty = Number(qty)

        if (!Number.isFinite(normalizedQty) || normalizedQty < 1) {
            setQty(1)
            return
        }

        if (normalizedQty > effectiveMax) {
            setQty(effectiveMax)
        }
    }

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>, delta: number) => {
        e.preventDefault()
        e.stopPropagation()
        stopSwiperAutoplay()
        setQty(clampQty(currentQty + delta, effectiveMax))
    }

    const iconButtonSx = {
        color: textColor,
        p: size === "small" ? 0.25 : 0.4,
        "& .MuiTouchRipple-root": {
            color: alpha(textColor, 0.55),
        },
    }

    return (
        <Stack spacing={0.2} direction="row" sx={{alignItems: "center", flexShrink: 0}}>
            <IconButton sx={iconButtonSx}
                        disableRipple={false}
                        size="small"
                        disabled={currentQty <= 1}
                        onClick={(e) => handleButtonClick(e, -1)}><RemoveOutlined/></IconButton>
            <TextField
                value={qty}
                name="qty"
                color="primary"
                variant="standard"
                autoComplete="off"
                sx={{
                    alignSelf: "center",
                    "& .MuiInputBase-root": {
                        border: `1px solid ${alpha(textColor, 0.45)}`,
                        borderRadius: size === "small" ? "5px" : "7px",
                        paddingX: size === "small" ? 0.35 : 0.55,
                        paddingY: size === "small" ? 0.15 : 0.35,
                        cursor: "text",
                        transition: "border-color 150ms ease",
                    },
                    "& .MuiInputBase-root.Mui-focused": {
                        borderColor: textColor,
                    },
                }}
                onFocus={stopSwiperAutoplay}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
                slotProps={{
                    input: {
                        disableUnderline: true,
                    },

                    htmlInput: {
                        inputMode: "numeric",
                        maxLength: String(effectiveMax).length,
                        "aria-label": "Quantity",
                        style: {
                            color: textColor,
                            textAlign: "center",
                            width: `${inputVisibleCharacters}ch`,
                            minWidth: "1ch",
                            fontWeight: 600,
                            padding: 0,
                            fontVariantNumeric: "tabular-nums",
                            fontSize: size === "small" ? "1.10rem" : "1.5rem",
                            cursor: "text",
                        }
                    }
                }}/>
            <IconButton sx={iconButtonSx}
                        disableRipple={false}
                        size="small"
                        disabled={currentQty >= effectiveMax}
                        onClick={(e) => handleButtonClick(e, 1)}><AddOutlined/></IconButton>
        </Stack>
    );
}

export default QtySelector
